import React, { useState, useRef, useEffect } from "react";

export interface SearchSelectProps {
	/**
   * Array de objetos a mostrar en el dropdown.
   */
  options: { value: string; label: string }[];
	/**
   * Texto auxiliar.
   */
  placeholder?: string;
  widthClass?: string;
	/**
   * Se llama cuando se selecciona un item.
   */
  onSelect?: (selectedValue: string) => void;
  /** Called on search input change */
  onSearch?: (searchQuery: string) => void;
	/**
   * Clases extra para el contenedor externo.
   */
  className?: string;
	/**
   * Prop to customize the icon
   */
  icon?: React.ReactNode;
	/**
   * Clase Tailwind opcional para establecer un min-width.
   * @default "min-w-[140px]"
   */
  minWidthClass?: string;
	/**
   * Clase Tailwind opcional para establecer un max-width.
   * @default "max-w-[504px]"
   */
  maxWidthClass?: string;
  /**
   * Optional label for the select component.
   */
  label?: string;
  /**
   * Left-side icon (e.g., location, search).
   */
  leftIcon?: React.ReactNode;
  defaultValue?: string;
  loading?: boolean,
}

const ChevronDownIcon = ({
  className = "",
  onClick,
}: {
  className?: string;
  onClick?: (e: React.MouseEvent<SVGSVGElement>) => void;
}) => (
  <svg
    className={`h-4 w-4 ${className}`}
    viewBox="0 0 20 20"
    fill="currentColor"
    onClick={onClick}
  >
    <path
      fillRule="evenodd"
      d="M10 12a.997.997 0 01-.707-.293l-4-4
         a1 1 0 111.414-1.414L10 9.586
         l3.293-3.293a1 1 0 011.414
         1.414l-4 4A.997.997 0 0110
         12z"
      clipRule="evenodd"
    />
  </svg>
);

const SearchSelect: React.FC<SearchSelectProps> = ({
  options,
  placeholder = "Search to Select",
  widthClass = "w-full",
  onSelect,
  onSearch,
  loading = false,
  className = "",
  icon,
  leftIcon,
  minWidthClass = "min-w-[140px]",
  maxWidthClass = "max-w-[504px]",
  label,
  defaultValue = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedValue, setSelectedValue] = useState<string | null>(defaultValue);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (value: string) => {
    setSelectedValue(value);
    setSearchQuery(""); // Reset search
    setIsOpen(false);
    onSelect?.(value);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value
    setSearchQuery(q)
    onSearch?.(q)
  }

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div ref={dropdownRef} className={`relative ${className} ${widthClass} ${minWidthClass} ${maxWidthClass}`}>
      {/* Label (if provided) */}
      {label && (
        <label className="block text-sm font-medium text-semantic-text-primary mb-1 text-left">
          {label}
        </label>
      )}

      <div
        className={`flex items-center justify-between w-full h-10 px-3 py-2 text-sm leading-tight 
          text-semantic-text-secondary bg-semantic-bg-primary-alt border border-semantic-border-secondary
          rounded-sm hover:bg-semantic-state-active transition-colors cursor-pointer`}
        onClick={() => {
          if (!loading) setIsOpen(true);
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        {/* Left Icon (if provided) */}
        {leftIcon && <div className="mr-2 text-current">{leftIcon}</div>}

        {/* Dynamic Search Input / Placeholder */}
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Type to search..."
            className="flex-1 bg-transparent outline-none text-sm text-left"
            autoFocus
          />
        ) : (
          <span className="flex-1 truncate text-sm text-left
          text-semantic-text-secondary">
            {selectedValue
            //If they’ve already picked something, show it
            ? options.find(o => o.value === selectedValue)?.label
            //Otherwise, if we’re loading, show spinner + placeholder
            : loading ? (
                <span className="inline-flex items-center">
                    <svg
                      className="animate-spin h-4 w-4 mr-2 text-current"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    {placeholder}
                  </span>
              ) : (
                //Fallback: just the placeholder
                placeholder
              )
          }
          </span>
        )}

        {/* Dropdown Arrow */}
        <div className="flex items-center">
          {icon || (
            <ChevronDownIcon
              className="ml-2 text-current"
              onClick={(e) => {
                e.stopPropagation();
                toggleDropdown();
              }}
            />
          )}
        </div>
      </div>

      {/* Dropdown Options */}
      {isOpen && (
        <div
          className={`absolute mt-1 w-full bg-semantic-bg-primary-alt border border-semantic-border-secondary 
          rounded-sm shadow-md z-50 ${minWidthClass}`}
        >
          <ul className="py-1 max-h-52 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <li className="px-3 py-2 text-sm text-semantic-text-primary cursor-default">No matches found</li>
            ) : (
              filteredOptions.map((option) => (
                <li key={option.value}>
                  <button
                    onClick={() => handleSelect(option.value)}
                    className="w-full text-left px-3 py-2 text-sm 
                      text-semantic-text-secondary hover:bg-semantic-state-active
                      hover:text-semantic-text-brand transition-colors"
                  >
                    {option.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchSelect;
